import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Box, ButtonBase, CircularProgress, Typography } from '@mui/material';
import { Iconify } from '../../../../../components';
import { describeNextTask, formatNextTaskTooltip } from '../../../../../utils/nextTaskStatus';

// Calmer status line (2026-10-01 user feedback: "I don't like the red and the
// title is too bold") — no filled pill, no coloured text. Just a 6px tone dot
// + grey text. Only the dot carries the tone colour; overdue/today reuse the
// same colours the pill used to (UpNextCardV2.jsx / ActionCards.jsx's
// getTimeBadgeStyle('due-today')), upcoming is a neutral text.disabled dot
// rather than introducing a new colour for something that isn't urgent.
const DOT_COLOR = {
    overdue: '#FF5630',
    today: '#d97706',
    upcoming: 'text.disabled',
};

// Separate component (not an inline renderCell closure) so each cell can hold
// its own "resolving the task id" busy state — onOpen(row) now does an async
// lookup (resolveNextTaskId) before the viewer opens, and a rep double-clicking
// before that resolves must not fire the lookup twice.
const NextTaskCellContent = ({ row, onOpen = undefined, now = undefined }) => {
    const [busy, setBusy] = useState(false);
    const { next_task_title: title, next_task_due_at: dueAt, overdue_task_count: overdueCount } = row;
    const status = describeNextTask(dueAt, now);
    const isClickable = Boolean(title) || overdueCount > 0;

    if (!isClickable) {
        return <Typography variant='body2' color='text.secondary'>—</Typography>;
    }

    const displayTitle = title || 'Overdue task';
    const dueTooltip = formatNextTaskTooltip(dueAt);
    const tooltipTitle = dueTooltip ? `${displayTitle} — ${dueTooltip}` : displayTitle;
    const leadName = row.lead_name || row.first_name || '';

    const handleClick = async (e) => {
        e.stopPropagation();
        if (busy) return; // guard against a second click landing before the id resolves
        setBusy(true);
        try {
            await onOpen?.(row);
        } catch (error) {
            // onOpen (resolveNextTaskId + opening the viewer) is not expected to
            // throw — defensive only, so a surprise rejection clears the spinner
            // instead of leaving the cell stuck busy / crashing as an unhandled
            // promise rejection.
            // eslint-disable-next-line no-console
            console.error('[NextTaskCell] onOpen failed', error);
        } finally {
            setBusy(false);
        }
    };

    return (
        <ButtonBase
            onClick={handleClick}
            disabled={busy}
            aria-label={`Open next task for ${leadName}: ${displayTitle}`}
            aria-busy={busy}
            title={tooltipTitle}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                justifyContent: 'flex-start',
                width: '100%',
                height: '100%',
                minWidth: 0,
                py: '6px',
                px: 1,
                gap: '2px',
                borderRadius: 1,
                textAlign: 'left',
                // Global CSS (src/App.css: `button { text-transform: capitalize
                // !important; }`) wins over a plain sx text-transform: none on the
                // ButtonBase root -- same specificity, and the global rule carries
                // !important, so this override must too.
                textTransform: 'none !important',
                position: 'relative',
                '&:hover': { bgcolor: 'action.hover' },
            }}
        >
            <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
                <Typography
                    noWrap
                    sx={{
                        fontWeight: 400,
                        fontSize: '13px',
                        color: 'text.primary',
                        flex: 1,
                        minWidth: 0,
                        textTransform: 'none',
                    }}
                >
                    {displayTitle}
                </Typography>
                {busy ? (
                    <CircularProgress size={14} sx={{ flexShrink: 0 }} />
                ) : (
                    <Iconify
                        icon='eva:chevron-right-fill'
                        width={14}
                        height={14}
                        sx={{
                            color: 'text.disabled',
                            flexShrink: 0,
                            opacity: 0.35,
                            transition: 'opacity 0.15s ease',
                            // Calm by default; only shows clearly once the user
                            // is hovering the row, so the column doesn't shout
                            // "click me" on every row.
                            '.MuiDataGrid-row:hover &': { opacity: 1 },
                        }}
                    />
                )}
            </Box>
            {status.label && (
                <Box
                    component='span'
                    data-tone={status.tone}
                    sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '12px',
                        color: 'text.secondary',
                        textTransform: 'none',
                        whiteSpace: 'nowrap',
                    }}
                >
                    <Box
                        component='span'
                        data-testid='next-task-status-dot'
                        sx={{
                            width: '6px',
                            height: '6px',
                            borderRadius: '50%',
                            bgcolor: DOT_COLOR[status.tone] || 'text.disabled',
                            flexShrink: 0,
                        }}
                    />
                    {status.label}
                </Box>
            )}
        </ButtonBase>
    );
};

NextTaskCellContent.propTypes = {
    row: PropTypes.shape({
        next_task_title: PropTypes.string,
        next_task_due_at: PropTypes.string,
        overdue_task_count: PropTypes.number,
        lead_name: PropTypes.string,
        first_name: PropTypes.string,
    }).isRequired,
    onOpen: PropTypes.func,
    now: PropTypes.instanceOf(Date),
};

// NewLeadDataGrid `next_task_inline` column (tasks mode only, newLeadsColumns).
//
// Reads row.next_task_title / row.next_task_due_at (BE contract:
// GET /practices/<id>/leads/?...&include_overdue_task_count=true) and shows
// the task title + a describeNextTask() status line (tone dot + grey text —
// calmer restyle, 2026-10-01). The whole cell is a button that opens the
// row's single-task viewer (TaskViewer) via
// `onOpen(row)` — the caller (NewLeadDataGrid.jsx) wires `onOpen` to a
// handler that resolves the task id (resolveNextTaskId) and opens TaskViewer,
// since the row itself only carries the title/due date, not an id.
//
// `now` is only ever overridden in tests — production callers rely on the
// describeNextTask/formatNextTaskTooltip default of `new Date()`.
const NextTaskCell = ({ onOpen, now } = {}) => ({
    field: 'next_task_inline',
    headerName: 'Next Task',
    width: 200,
    // No confirmed backend ordering support for next_task_due_at on the leads
    // list endpoint yet — leave client/server sorting off rather than silently
    // sorting a partial in-memory page.
    // TODO(next-task-sort): enable once BE supports ordering=next_task_due_at
    sortable: false,
    renderCell: (params) => (
        <NextTaskCellContent row={params?.row || {}} onOpen={onOpen} now={now} />
    ),
});

export default NextTaskCell;
